import { normalizeSearch, formatName } from '../lib/format';
import FileDrop from './ui/FileDrop';
import FieldLabel from './ui/FieldLabel';
import Button from './ui/Button';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import PdfViewer from './PdfViewer';
import { convertHeicBlob } from '../heicImage';
import { resolveStorageFileUrl, uploadFileToStorage } from '../storageUpload';
export default function MyDropzoneMulti({
    label,
    required,
    disabled,
    storageLocation,
    errors,
    name,
    editing,
    images,
    updateImages,
    allowPdf = false,
    allowPdfOnly = false,
    allowVideo = false,
    customMessage,
    setUploading,
    allowMultiple = true,
    cameraOnly = false,
    hidePreviews = false
}) {
    const activeOrg = useSelector(state => state.user.activeOrg)
    const [pdfStates, setPdfStates] = useState({})
    const [previewUrls, setPreviewUrls] = useState({})
    const normalizedImages = useMemo(
        () => (Array.isArray(images) ? images : images ? [images] : []),
        [images]
    )

    useEffect(() => {
        let active = true
        Promise.all(normalizedImages.map(async file => {
            const source = typeof file === 'string' ? file : file?.url
            if (!source || /^https?:|^blob:|^data:/i.test(source)) return [source, source]
            const resolved = await resolveStorageFileUrl(file, activeOrg?.id || '')
            return [source, resolved]
        })).then(entries => {
            if (active) setPreviewUrls(Object.fromEntries(entries.filter(([source, url]) => source && url)))
        })
        return () => { active = false }
    }, [normalizedImages, activeOrg?.id])

    const onDrop = useCallback(async (acceptedFiles) => {
        if (!acceptedFiles.length) return
        if (setUploading) setUploading(true)

        try {
            let updated = [...normalizedImages]

            for (let file of acceptedFiles) {
                let uploadFile = file

            // HEIC → PNG
            if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
                try {
                    const convertedBlob = await convertHeicBlob(file, 'image/png')
                    uploadFile = new File(
                        [convertedBlob],
                        file.name.toLowerCase().endsWith('.heic')
                            ? file.name.replace(/\.heic$/i, '.png')
                            : `${file.name}.png`,
                        { type: 'image/png' }
                    )
                } catch (err) {
                    console.error('HEIC conversion error:', err)
                }
            }

                const path = `${storageLocation}/${Date.now()}_${uploadFile.name}`
                const uploaded = await uploadFileToStorage({
                    file: uploadFile,
                    path,
                    mimeType: uploadFile.type || '',
                    fallbackName: uploadFile.name,
                    orgId: activeOrg?.id || '',
                })
                updated.push({
                    ...uploaded,
                    name: uploaded.name || uploadFile.name,
                    pdf: uploadFile.type === 'application/pdf' || uploadFile.name.toLowerCase().endsWith('.pdf'),
                })
            }

            updateImages(updated)
        } finally {
            if (setUploading) setUploading(false)
        }
    }, [normalizedImages, setUploading, storageLocation, updateImages, activeOrg?.id])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: allowMultiple,
        disabled,
        accept: allowVideo
            ? { 'video/*': [] }
            : allowPdfOnly
                ? { 'application/pdf': [] }
                : allowPdf
                    ? { 'image/*': [], 'application/pdf': [] }
                    : { 'image/*': [] },
    })

    const handleRemove = (index) => {
        const updated = normalizedImages.filter((_, i) => i !== index)
        const file = normalizedImages[index]
        const url = typeof file === 'string' ? file : file?.url
        if (url) {
            const copy = { ...pdfStates }
            delete copy[url]
            setPdfStates(copy)
        }
        updateImages(updated)
    }

    const handlePDFLoad = (url, numPages) => {
        setPdfStates(prev => ({ ...prev, [url]: { page: 1, pages: numPages } }))
    }

    const setPageNumber = (url, nextPage) => {
        setPdfStates(prev => {
            const state = prev[url] || { page: 1, pages: 1 }
            const resolvedPage = typeof nextPage === 'function' ? nextPage(state.page) : nextPage
            const newPage = Math.min(state.pages, Math.max(1, resolvedPage))
            return { ...prev, [url]: { ...state, page: newPage } }
        })
    }

    return (
        <div style={{ width: '100%', marginTop: '15px' }}>
            {label && (
                <FieldLabel className="fieldLabel">
                    {label} {required && <span className="uiRequired" style={{ color: 'red' }}>*</span>}
                </FieldLabel>
            )}

            {editing && !disabled && (
                <FileDrop active={isDragActive} disabled={disabled}
                    {...getRootProps()}
                    style={{display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginTop: 8, boxSizing: 'border-box', transition: 'border-color .15s ease-in-out, background-color .15s ease-in-out'}}
                >
                    {/* <input {...getInputProps()} /> */}

                    <input
                        {...getInputProps()}
                        capture={cameraOnly ? "environment" : undefined}
                    />
                    <FileDrop.Prompt hint={customMessage} />
                </FileDrop>
            )}

            {!hidePreviews && <div style={{ marginTop: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {normalizedImages.map((file, idx) => {
                    const url = typeof file === 'string' ? file : file?.url
                    if (!url) return null
                    const previewUrl = previewUrls[url] || (/^https?:|^blob:|^data:/i.test(url) ? url : '')

                    const displayName = typeof file === 'string'
                        ? (url.split('/').pop() || 'Document').split('?')[0]
                        : (file?.name || (url.split('/').pop() || 'Document').split('?')[0])
                    const isPDF = !!file?.pdf || displayName.toLowerCase().endsWith('.pdf') || normalizeSearch(url).includes('.pdf')

                    return (
                        <div
                            key={idx}
                            style={{
                                border: '1px solid #e2e8f0',
                                padding: 8,
                                borderRadius: 8,
                                background: '#fff',
                                position: 'relative',
                                // maxWidth: 300
                            }}
                        >
                            {editing && !disabled && (
                                <Button variant="secondary" size="icon"
                                    type="button"
                                    aria-label="Remove attachment"
                                    onClick={() => handleRemove(idx)}
                                    style={{position: 'absolute', top: 6, right: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 0, cursor: 'pointer', zIndex: 10}}
                                >
                                    ×
                                </Button>
                            )}

                            {isPDF ? (previewUrl ? (
                                <>
                                    <PdfViewer
                                        showSmall
                                        file={previewUrl}
                                        pageNumber={pdfStates[url]?.page || 1}
                                        setPageNumber={(nextPage) => setPageNumber(url, nextPage)}
                                        numPages={pdfStates[url]?.pages || 1}
                                        setNumPages={(pages) => handlePDFLoad(url, pages)}
                                    />
                                    <div style={{ marginTop: 8, fontSize: 13, color: '#64748b', wordBreak: 'break-word', maxWidth: 300 }}>
                                        {formatName(displayName)}
                                    </div>
                                </>
                            ) : <div style={{ padding: 24, color: '#64748b' }}>Preparing preview...</div>) : (
                                previewUrl ? <img
                                    src={previewUrl}
                                    alt="uploaded"
                                    style={{ width: '100%', maxWidth: 300, maxHeight: 300, objectFit: 'contain' }}
                                /> : <div style={{ padding: 24, color: '#64748b' }}>Preparing preview...</div>
                            )}

                            {/* <div style={{ marginTop: 8, fontSize: 13, wordBreak: 'break-all' }}>
                                {url}
                            </div> */}
                        </div>
                    )
                })}
            </div>}
        </div>
    )
}
